import { CopyOutlined } from '@ant-design/icons'
import type { TablePaginationConfig, TableProps } from 'antd'
import { Button, Card, message, Modal, Table, Tooltip } from 'antd'
import copy from 'copy-to-clipboard'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import { FEATURE } from 'constants/feature'
import { PAGES } from 'routes'
import { logError, logInfo } from 'utils/logger'

import './style.scss'

const REQUEST_TYPE = { p2p: 'LENDING', pto: 'PTO' }
const APPROVE_TYPE = { done: 2, pending: 1, reject: -1 }

const ListPortal = () => {
  const additionalItems = [{ title: 'Portal' }]
  const [data, setData] = useState<any[]>([])
  const [totalCount, setTotalCount]: any = useState(0)
  const [loading, setLoading] = useState(false)
  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const getListPortal = useCallback(async () => {
    try {
      setLoading(true)
      const listFromApi = await AppAdmin.getListPortal(current, pageSize)
      const list = listFromApi.data.data.map((item: any, index: number) => ({
        index: index + 1,
        name: item?.property_name,
        ownerAddress: item?.ownerAddress,
        is_lending: item?.is_lending,
        is_pto: item?.is_pto,
        _id: item._id,
      }))
      setData(list)
      logInfo(list)
      setTotalCount(listFromApi?.data?.totalCount || 0)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setData([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [current, pageSize])

  useEffect(() => {
    getListPortal()
  }, [current, getListPortal, pageSize])

  const handleApprove = async (code: string, type: string, approve: number) => {
    try {
      await AppAdmin.approveRequest({
        code,
        type,
        approve,
      })
      message.success('Success to approve request!')
    } catch (error: any) {
      logError(error)
      message.error(error?.message || 'Fail to approve request!')
    } finally {
      getListPortal()
    }
  }

  const navigate = useNavigate()

  const columns: TableProps<any>['columns'] = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Address',
      dataIndex: 'ownerAddress',
      key: 'ownerAddress',
      width: 200,
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a target="_blank" rel="noreferrer" href={`https://basescan.org/address/${text}`}>
            {text?.slice(0, 6)}... {text?.slice(-6)}
          </a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'P2P request',
      dataIndex: 'is_lending',
      key: 'is_lending',
      render: (_, record) =>
        record?.is_lending === 1 ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <ProtectedButton
              feature={FEATURE.PORTAL_TOKENIZE}
              requireCRUD
              onClick={() => navigate(`/p2plending/create?code=${record?._id}`)}
              style={{ width: 150 }}
              type="primary"
            >
              Approve
            </ProtectedButton>
            <ProtectedButton
              feature={FEATURE.PORTAL_TOKENIZE}
              requireCRUD
              onClick={() =>
                Modal.confirm({
                  title: `Are you sure you want to reject this request?`,
                  content: `This action will reject user creating P2P lending request.`,
                  okText: 'Yes, reject',
                  cancelText: 'Cancel',
                  centered: true,
                  onOk: () => {
                    handleApprove(record?._id, REQUEST_TYPE.p2p, APPROVE_TYPE.reject)
                  },
                })
              }
              style={{ width: 150 }}
            >
              Reject
            </ProtectedButton>
          </div>
        ) : (
          <>No request</>
        ),
    },
    {
      title: 'PTO request',
      dataIndex: 'is_pto',
      key: 'is_pto',
      render: (_, record) =>
        record?.is_pto === 1 ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <ProtectedButton
              feature={FEATURE.PORTAL_TOKENIZE}
              requireCRUD
              onClick={() => navigate(`${PAGES.PTO}/create?code=${record?._id}`)}
              style={{ width: 150 }}
              type="primary"
            >
              Approve
            </ProtectedButton>
            <ProtectedButton
              feature={FEATURE.PORTAL_TOKENIZE}
              requireCRUD
              onClick={() =>
                Modal.confirm({
                  title: `Are you sure you want to reject this request?`,
                  content: `This action will reject user creating P2P lending request.`,
                  okText: 'Yes, reject',
                  cancelText: 'Cancel',
                  centered: true,
                  onOk: () => {
                    handleApprove(record?._id, REQUEST_TYPE.pto, APPROVE_TYPE.reject)
                  },
                })
              }
              style={{ width: 150 }}
            >
              Reject
            </ProtectedButton>
          </div>
        ) : (
          <>No request</>
        ),
    },
  ]

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || 1)
    setPageSize(newPageSize || 10)
  }

  return (
    <>
      <div className="main-section user">
        <Breadcrumb additionalItems={additionalItems} />
        <Card style={{ margin: '0 auto' }}>
          <Table<any>
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={totalCount < pageSize ? false : { total: totalCount, current, pageSize }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            bordered
          />
        </Card>
      </div>
    </>
  )
}

export default ListPortal
